import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import {
  Notification,
  FetchNotificationsApiResponse,
  createNotification,
} from 'types/notification';

/**
 * お知らせ一覧画面の状態
 */
interface ListState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * 現在のページ
   */
  currentPage: number;
  /**
   * 最後のページ（ページの総数）
   */
  lastPage: number;
  /**
   * アイテムの総数
   */
  totalCount: number;
  /**
   * お知らせ一覧 for ui
   */
  notifications: Notification[];
  /**
   * 選択されたお知らせ
   */
  selectedNotification: Notification | undefined;
  /**
   * お知らせ一覧の最終更新日時
   * NOTE: この値が更新されるとお知らせがリロードされる
   */
  finalUpdateTime: string | undefined;
}

/**
 * お知らせ一覧画面の初期状態
 */
const initialState: ListState = {
  loading: false,
  currentPage: 1,
  lastPage: 1,
  totalCount: 0,
  notifications: [],
  selectedNotification: undefined,
  finalUpdateTime: undefined,
};

/**
 * お知らせ一覧画面の slice(reducer + action)
 */
export const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchNotificationsSuccess: (
      state,
      action: PayloadAction<{
        apiResponse: FetchNotificationsApiResponse;
        fetchedAt: string;
      }>,
    ) => {
      const { apiResponse, fetchedAt } = action.payload;

      state.currentPage = apiResponse.currentPage;
      state.lastPage = apiResponse.lastPage;
      state.totalCount = apiResponse.totalCount;
      state.notifications = apiResponse.notifications.map((item) =>
        createNotification(item, fetchedAt),
      );
    },
    notificationSelected: (
      state,
      action: PayloadAction<{ notification: Notification }>,
    ) => {
      state.selectedNotification = action.payload.notification;
    },
    selectedNotificationCleared: (state) => {
      state.selectedNotification = undefined;
    },
    finalUpdateTimeUpdated: (
      state,
      action: PayloadAction<{ finalUpdateTime: string }>,
    ) => {
      state.finalUpdateTime = action.payload.finalUpdateTime;
    },
  },
});

/**
 * お知らせ一覧画面の action
 */
export const listActions = listSlice.actions;

/**
 * お知らせ一覧画面の selector
 */
export const listSelectors = {
  loading: (state: RootState) => state.notification.list.loading,
  currentPage: (state: RootState) => state.notification.list.currentPage,
  lastPage: (state: RootState) => state.notification.list.lastPage,
  totalCount: (state: RootState) => state.notification.list.totalCount,
  selectedNotification: (state: RootState) =>
    state.notification.list.selectedNotification,
  notifications: (state: RootState) => state.notification.list.notifications,
  finalUpdateTime: (state: RootState) =>
    state.notification.list.finalUpdateTime,
} as const;
